import {
  useCallback, useEffect, useId, useRef, useState,
  type FormEvent, type KeyboardEvent, type ReactNode,
} from 'react'

import { AdminApiError } from '../api/AdminApiClient'

// Modal dialog used by every audited intervention (reset / revoke /
// daemon trigger). Capture-the-reason is the universal pattern in
// plan-admin/06: every operator action that mutates server state
// requires a free-text reason that becomes part of the audit row.
//
// The dialog is intentionally generic: the host page wires up the
// mutation (via `useAdminMutation`) and supplies copy + confirm
// behaviour; the dialog only owns reason validation, focus
// management, and the optional confirm-twice flow.

const MIN_REASON_LENGTH = 4
const MAX_REASON_LENGTH = 500

export interface ConfirmActionPayload {
  reason: string
}

export interface ConfirmActionDialogProps {
  title: string
  body: ReactNode
  confirmLabel: string
  // When true, the dialog requires a second confirmation step before
  // calling `onConfirm`. Used for irreversible actions (revoke).
  confirmTwice?: boolean
  secondConfirmBody?: ReactNode
  onConfirm: (payload: ConfirmActionPayload) => Promise<void> | void
  onCancel: () => void
  // Render-time error to surface (e.g. last mutation failure) without
  // having to wait for another submit. Useful when the host page is
  // also storing the error in TanStack mutation state.
  initialError?: string
  // Map raw backend error codes to operator-friendly copy. The dialog
  // still records the raw code when nothing maps so logs/tests stay
  // diagnosable, but the visible string is the mapped message.
  // (review_comments L1, HEAD db4990e — `RATE_LIMITED` →
  // "Already triggered within the last minute.")
  errorMessages?: Record<string, string>
}

export function ConfirmActionDialog({
  title, body, confirmLabel, confirmTwice = false,
  secondConfirmBody = 'Click again to confirm. This cannot be undone.',
  onConfirm, onCancel, initialError = '', errorMessages,
}: ConfirmActionDialogProps) {
  const titleId = useId()
  const bodyId = useId()
  const inputId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerOriginRef = useRef<Element | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [reason, setReason] = useState('')
  const [stage, setStage] = useState<'first' | 'second'>('first')
  const [error, setError] = useState(initialError)
  const [submitting, setSubmitting] = useState(false)

  // Remember the focus origin so we can restore on unmount. Tests
  // running in jsdom don't always have an active element, hence the
  // null-safe guard.
  useEffect(() => {
    triggerOriginRef.current = document.activeElement
    inputRef.current?.focus()
    return () => {
      const origin = triggerOriginRef.current
      if (origin && 'focus' in origin && typeof (origin as HTMLElement).focus === 'function') {
        try { (origin as HTMLElement).focus() } catch { /* noop */ }
      }
    }
  }, [])

  const trimmed = reason.trim()
  const tooShort = trimmed.length < MIN_REASON_LENGTH
  const tooLong = trimmed.length > MAX_REASON_LENGTH
  const reasonValid = !tooShort && !tooLong

  const submit = useCallback(async () => {
    if (!reasonValid || submitting) return
    if (confirmTwice && stage === 'first') {
      setStage('second')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      await onConfirm({ reason: trimmed })
    } catch (e) {
      if (e instanceof AdminApiError) {
        const code = e.errorCode || `HTTP ${e.status}`
        setError(errorMessages?.[code] ?? code)
      } else if (e instanceof Error) {
        setError(errorMessages?.[e.message] ?? e.message)
      } else {
        setError(errorMessages?.UNKNOWN_ERROR ?? 'UNKNOWN_ERROR')
      }
      // Drop back to the first panel so the operator can fix the
      // reason and try again. Keeps the second-panel UX honest:
      // every confirm is a fresh "I really mean it".
      if (confirmTwice) setStage('first')
    } finally {
      setSubmitting(false)
    }
  }, [confirmTwice, errorMessages, onConfirm, reasonValid, stage, submitting,
      trimmed])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && !submitting) {
      event.stopPropagation()
      onCancel()
      return
    }
    // Focus trap: keep Tab/Shift+Tab inside the dialog so keyboard
    // users cannot land on controls behind the modal while a
    // destructive action is in flight. (review_comments M4, HEAD
    // db4990e.) We compute the tabbable set fresh each Tab so dynamic
    // panels (e.g. the second-confirm body) are picked up immediately.
    if (event.key === 'Tab') {
      const root = containerRef.current
      if (!root) return
      const tabbables = collectTabbables(root)
      if (tabbables.length === 0) {
        event.preventDefault()
        return
      }
      const first = tabbables[0]
      const last = tabbables[tabbables.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (active === last || !root.contains(active)) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  }, [onCancel, submitting])

  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault()
    void submit()
  }, [submit])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
                 bg-stone-900/40 px-4"
      role="presentation"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg"
      >
        <h2 id={titleId} className="text-base font-semibold text-stone-900">
          {title}
        </h2>
        <div id={bodyId} className="mt-2 text-sm text-stone-700">
          {body}
        </div>

        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor={inputId}
              className="block text-xs font-medium uppercase tracking-wide
                         text-stone-600"
            >
              Reason (required)
            </label>
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={MAX_REASON_LENGTH + 50}
              disabled={submitting}
              className="mt-1 w-full rounded-md border border-stone-300 px-2 py-1
                         text-sm shadow-sm focus:border-accent focus:outline-none
                         focus:ring-1 focus:ring-accent disabled:bg-stone-50"
              placeholder="e.g. SUPP-1234 — escalated by user reply"
            />
            <p className="mt-1 text-xs text-stone-500">
              {trimmed.length}/{MAX_REASON_LENGTH} characters; minimum
              {' '}{MIN_REASON_LENGTH}.
            </p>
          </div>

          {confirmTwice && stage === 'second' && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-3
                         py-2 text-sm text-amber-900"
              role="alert"
            >
              {secondConfirmBody}
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2
                         text-sm text-red-800"
              role="alert"
              data-testid="confirm-action-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-md border border-stone-300 bg-white px-3 py-1
                         text-sm text-stone-700 hover:bg-stone-50
                         disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reasonValid || submitting}
              className="rounded-md bg-accent px-3 py-1 text-sm font-medium
                         text-white shadow-sm hover:bg-accent/90
                         disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? 'Working…'
                : confirmTwice && stage === 'second'
                  ? `Confirm ${confirmLabel.toLowerCase()}`
                  : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function collectTabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR))
    .filter((el) => el.tabIndex !== -1)
}
